import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow, isPast } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { formatCost } from "@/lib/llmModels";
import { AlertTriangle, Bot, CheckCircle2, ChevronDown, Loader2, XCircle } from "lucide-react";
import { ModelPickerButton } from "./ModelPicker";
import type { AdminDriver, AdminRace, Matchup } from "./types";

type LlmBot = {
  id: string;
  provider: string;
  display_name: string;
  model: string;
  enabled: boolean | null;
};

type BotStatus = { matchup_picks: number; has_race_pick: boolean };

type TipsResult = {
  matchups_inserted: number;
  picks: { matchup_id: string; selected_driver_number: number; reasoning: string }[];
  pole: number;
  pole_margin_ms: number;
  pole_reasoning: string;
  sprint: { p1_driver_number: number; p2_driver_number: number; p3_driver_number: number; reasoning: string } | null;
  usage: { cost_usd: number | null; elapsed_ms: number };
};

type RunState =
  | { state: "idle" }
  | { state: "running"; startedAt: number }
  | { state: "done"; result: TipsResult }
  | { state: "error"; message: string };

type LastPractice = { session_name: string; date_end: string } | null;

const LlmTipsTab = ({
  race,
  driverMap,
  matchups,
}: {
  race: AdminRace | undefined;
  driverMap: Record<number, AdminDriver>;
  matchups: Matchup[];
}) => {
  const queryClient = useQueryClient();
  const [runStates, setRunStates] = useState<Record<string, RunState>>({});
  const [running, setRunning] = useState(false);

  const { data: bots = [] } = useQuery({
    queryKey: ["admin-llm-bots"],
    queryFn: async () => {
      const { data, error } = await supabase.from("llm_bots").select("*").order("provider");
      if (error) throw error;
      return data as LlmBot[];
    },
  });

  const { data: statuses, refetch: refetchStatuses } = useQuery({
    queryKey: ["admin-llm-tips-status", race?.round_number],
    queryFn: () =>
      invokeEdgeFunction<{ statuses: Record<string, BotStatus> }>("generate-llm-tips", {
        round: race!.round_number,
        check_only: true,
      }).then((r) => r.statuses),
    enabled: !!race,
  });

  // The last practice session decides when the bots have the most information:
  // P3 on a normal weekend, P1 on a sprint weekend. Read-only, public table.
  const { data: lastPractice } = useQuery<LastPractice>({
    queryKey: ["admin-last-practice", race?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sessions")
        .select("session_name, date_end")
        .eq("race_id", race!.id)
        .eq("session_type", "Practice")
        .order("date_end", { ascending: false })
        .limit(1);
      if (error) throw error;
      const row = data?.[0];
      return row && row.date_end ? { session_name: row.session_name, date_end: row.date_end } : null;
    },
    enabled: !!race,
  });

  const toggleEnabled = async (bot: LlmBot) => {
    const { error } = await supabase.from("llm_bots").update({ enabled: !bot.enabled }).eq("id", bot.id);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else queryClient.invalidateQueries({ queryKey: ["admin-llm-bots"] });
  };

  const setModel = async (bot: LlmBot, model: string) => {
    const { error } = await supabase.from("llm_bots").update({ model }).eq("id", bot.id);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else queryClient.invalidateQueries({ queryKey: ["admin-llm-bots"] });
  };

  const runBot = async (bot: LlmBot) => {
    setRunStates((prev) => ({ ...prev, [bot.id]: { state: "running", startedAt: Date.now() } }));
    try {
      const result = await invokeEdgeFunction<TipsResult>("generate-llm-tips", {
        round: race!.round_number,
        bot_id: bot.id,
      });
      setRunStates((prev) => ({ ...prev, [bot.id]: { state: "done", result } }));
      return true;
    } catch (e) {
      setRunStates((prev) => ({ ...prev, [bot.id]: { state: "error", message: (e as Error).message } }));
      return false;
    }
  };

  // Every enabled bot runs at once — each is its own edge invocation with its own
  // wall clock, so the wait is the slowest bot rather than the sum of all three.
  const generateAll = async () => {
    if (!race) return;
    const targets = bots.filter((b) => b.enabled);
    setRunning(true);
    const outcomes = await Promise.all(targets.map(runBot));
    setRunning(false);
    refetchStatuses();

    const ok = outcomes.filter(Boolean).length;
    const failed = targets.filter((_, i) => !outcomes[i]).map((b) => b.display_name);
    if (failed.length === 0) {
      toast({ title: `Tips in for ${ok} of ${targets.length} bot${targets.length === 1 ? "" : "s"}` });
    } else {
      toast({
        title: `Tips in for ${ok} of ${targets.length} — ${failed.join(", ")} failed`,
        variant: "destructive",
      });
    }
  };

  if (!race) return <p className="text-sm text-muted-foreground">Select a round first.</p>;

  const enabledCount = bots.filter((b) => b.enabled).length;
  const publishedCount = matchups.filter((m) => m.status === "published").length;
  const practiceDone = lastPractice ? isPast(new Date(lastPractice.date_end)) : false;
  const canGenerate = !running && enabledCount > 0 && publishedCount > 0;

  const acronym = (num: number) => driverMap[num]?.name_acronym ?? `#${num}`;
  const matchupLabel = (id: string) => {
    const m = matchups.find((x) => x.id === id);
    return m ? `${acronym(m.driver_a_number)} vs ${acronym(m.driver_b_number)}` : "?";
  };

  return (
    <div className="space-y-4">
      {/* Readiness */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <ReadinessRow
            ok={publishedCount > 0}
            label="Matchups published"
            detail={
              matchups.length === 0
                ? "none yet"
                : `${publishedCount} of ${matchups.length}${publishedCount === 0 ? " — publish before generating" : ""}`
            }
          />
          <ReadinessRow
            ok={practiceDone}
            label="Last practice"
            detail={
              lastPractice
                ? `${lastPractice.session_name}, ${format(new Date(lastPractice.date_end), "EEE d MMM HH:mm")} · ${
                    practiceDone
                      ? `finished ${formatDistanceToNow(new Date(lastPractice.date_end))} ago`
                      : `in ${formatDistanceToNow(new Date(lastPractice.date_end))}`
                  }`
                : "no practice sessions seeded"
            }
            softWarning
          />
        </CardContent>
      </Card>

      {/* Bots */}
      <div className="space-y-2">
        {bots.map((bot) => {
          const status = statuses?.[bot.id];
          const run = runStates[bot.id] ?? { state: "idle" };
          return (
            <Card key={bot.id} className={bot.enabled ? "" : "opacity-60"}>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center gap-3">
                  <Bot className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">{bot.display_name}</span>
                      {status && status.matchup_picks > 0 && run.state === "idle" && (
                        <Badge className="bg-green-100 text-[10px] text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          {status.matchup_picks} picks in{status.has_race_pick ? " + pole" : ""}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1">
                      <ModelPickerButton
                        value={bot.model}
                        onChange={(id) => setModel(bot, id)}
                        disabled={running}
                        title={`Model for ${bot.display_name}`}
                      />
                    </div>
                  </div>
                  <Switch checked={!!bot.enabled} onCheckedChange={() => toggleEnabled(bot)} disabled={running} />
                </div>

                {run.state === "running" && (
                  <p className="flex items-center gap-1.5 pl-8 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Researching and picking…
                  </p>
                )}
                {run.state === "error" && (
                  <p className="flex items-start gap-1.5 pl-8 text-xs text-destructive">
                    <XCircle className="mt-0.5 h-3 w-3 shrink-0" /> {run.message}
                  </p>
                )}
                {run.state === "done" && (
                  <div className="pl-8">
                    <p className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle2 className="h-3 w-3 shrink-0" />
                      {run.result.matchups_inserted} picks · pole {acronym(run.result.pole)} ·{" "}
                      {formatCost(run.result.usage.cost_usd)} · {Math.round(run.result.usage.elapsed_ms / 1000)}s
                    </p>
                    <Collapsible>
                      <CollapsibleTrigger className="mt-1 flex items-center gap-1 text-[11px] text-primary hover:underline">
                        Reasoning <ChevronDown className="h-3 w-3" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2 space-y-1.5 text-xs">
                        {run.result.picks.map((p) => (
                          <div key={p.matchup_id} className="grid grid-cols-[auto_auto_1fr] items-baseline gap-x-2">
                            <span className="text-muted-foreground">{matchupLabel(p.matchup_id)}</span>
                            <span className="font-semibold">{acronym(p.selected_driver_number)}</span>
                            <span className="text-muted-foreground">{p.reasoning}</span>
                          </div>
                        ))}
                        <div className="grid grid-cols-[auto_auto_1fr] items-baseline gap-x-2 border-t pt-1.5">
                          <span className="text-muted-foreground">Pole</span>
                          <span className="font-semibold">
                            {acronym(run.result.pole)} +{(run.result.pole_margin_ms / 1000).toFixed(3)}s
                          </span>
                          <span className="text-muted-foreground">{run.result.pole_reasoning}</span>
                        </div>
                        {run.result.sprint && (
                          <div className="grid grid-cols-[auto_auto_1fr] items-baseline gap-x-2">
                            <span className="text-muted-foreground">Sprint</span>
                            <span className="font-semibold">
                              {acronym(run.result.sprint.p1_driver_number)} · {acronym(run.result.sprint.p2_driver_number)} ·{" "}
                              {acronym(run.result.sprint.p3_driver_number)}
                            </span>
                            <span className="text-muted-foreground">{run.result.sprint.reasoning}</span>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Button className="w-full font-semibold" disabled={!canGenerate} onClick={generateAll}>
        {running ? (
          <>
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Generating…
          </>
        ) : (
          `Generate tips for Round ${race.round_number} (${enabledCount} bot${enabledCount === 1 ? "" : "s"})`
        )}
      </Button>
      <p className="text-center text-[11px] text-muted-foreground">
        Every bot gets the identical prompt. Re-running is safe — picks are upserted, so tips are simply replaced.
      </p>
    </div>
  );
};

function ReadinessRow({
  ok,
  label,
  detail,
  softWarning,
}: {
  ok: boolean;
  label: string;
  detail: string;
  softWarning?: boolean;
}) {
  const Icon = ok ? CheckCircle2 : softWarning ? AlertTriangle : XCircle;
  const colour = ok
    ? "text-green-600 dark:text-green-400"
    : softWarning
    ? "text-amber-600 dark:text-amber-400"
    : "text-destructive";
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${colour}`} />
      <div className="min-w-0">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground"> — {detail}</span>
      </div>
    </div>
  );
}

export default LlmTipsTab;
